'use client';

/**
 * Consent Context Panel Component
 *
 * Displays consent information for referrals created from employee program consent.
 * Shows:
 * - Program and employer information
 * - Consent status and date
 * - Employee details
 * - Link type (auto_created vs manual_created)
 */

import React, { useState, useEffect } from 'react';
import { useAppTranslation } from '@/contexts/TranslationContext';
import { useFormattedDate } from '../../hooks/useUserTimezone';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  FileText,
  User,
  Building2,
  Calendar,
  CheckCircle,
  Clock,
  AlertCircle,
  ChevronDown,
  ChevronUp,
  ExternalLink,
  Shield,
  Loader2,
} from 'lucide-react';
import {
  serviceReferralsAPI,
  ConsentContext,
} from '@/services/api/serviceReferrals';

// ============================================================================
// TYPE DEFINITIONS
// ============================================================================

interface ConsentContextPanelProps {
  referralId: number;
  className?: string;
  collapsed?: boolean;
  onViewConsent?: (consentId: number) => void;
}

// ============================================================================
// COMPONENT
// ============================================================================

export function ConsentContextPanel({
  referralId,
  className = '',
  collapsed: initialCollapsed = true,
  onViewConsent,
}: ConsentContextPanelProps) {
  const { t } = useAppTranslation();
  const { fmt } = useFormattedDate();
  const [consentContext, setConsentContext] = useState<ConsentContext | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isExpanded, setIsExpanded] = useState(!initialCollapsed);

  // Load consent context
  useEffect(() => {
    const loadConsentContext = async () => {
      setLoading(true);
      setError(null);
      try {
        const context = await serviceReferralsAPI.getConsentContext(referralId);
        setConsentContext(context);
      } catch (err) {
        console.error('Failed to load consent context:', err);
        setError('Failed to load consent information');
      } finally {
        setLoading(false);
      }
    };

    loadConsentContext();
  }, [referralId]);

  // Format date for display
  const formatDate = (dateStr: string | undefined) => {
    if (!dateStr) return 'N/A';
    return fmt.medium(dateStr);
  };

  // Get consent status badge
  const getConsentStatusBadge = (status: string) => {
    switch (status) {
      case 'accepted':
        return (
          <Badge className="bg-green-100 text-green-800 border-green-200 dark:bg-green-900/50 dark:text-green-200 dark:border-green-700">
            <CheckCircle className="w-3 h-3 mr-1" />
            {t('serviceReferrals.consent.statusAccepted', 'Accepted')}
          </Badge>
        );
      case 'pending':
        return (
          <Badge className="bg-yellow-100 text-yellow-800 border-yellow-200 dark:bg-yellow-900/50 dark:text-yellow-200 dark:border-yellow-700">
            <Clock className="w-3 h-3 mr-1" />
            {t('serviceReferrals.consent.statusPending', 'Pending')}
          </Badge>
        );
      case 'revoked':
        return (
          <Badge className="bg-red-100 text-red-800 border-red-200 dark:bg-red-900/50 dark:text-red-200 dark:border-red-700">
            <AlertCircle className="w-3 h-3 mr-1" />
            {t('serviceReferrals.consent.statusRevoked', 'Revoked')}
          </Badge>
        );
      default:
        return <Badge variant="outline">{status}</Badge>;
    }
  };

  // Get link type badge
  const getLinkTypeBadge = (linkType: string) => {
    if (linkType === 'auto_created') {
      return (
        <Badge className="bg-blue-100 text-blue-800 border-blue-200 dark:bg-blue-900/50 dark:text-blue-200 dark:border-blue-700">
          {t('serviceReferrals.consent.autoCreated', 'Auto-Created')}
        </Badge>
      );
    }
    return (
      <Badge className="bg-purple-100 text-purple-800 border-purple-200 dark:bg-purple-900/50 dark:text-purple-200 dark:border-purple-700">
        {t('serviceReferrals.consent.manual', 'Manual')}
      </Badge>
    );
  };

  // Loading state
  if (loading) {
    return (
      <Card className={`${className}`}>
        <CardContent className="p-4">
          <div className="flex items-center gap-2 text-gray-500">
            <Loader2 className="w-4 h-4 animate-spin" />
            <span>{t('serviceReferrals.consent.loadingConsent', 'Loading consent information...')}</span>
          </div>
        </CardContent>
      </Card>
    );
  }

  // No consent context (not a consent-based referral)
  if (!consentContext) {
    return null;
  }

  // Error state
  if (error) {
    return (
      <Card className={`border-red-200 ${className}`}>
        <CardContent className="p-4">
          <div className="flex items-center gap-2 text-red-600">
            <AlertCircle className="w-4 h-4" />
            <span>{error}</span>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className={`border-blue-200 bg-blue-50/30 dark:bg-blue-900/10 ${className}`}>
      <CardHeader
        className="p-4 pb-2 cursor-pointer"
        onClick={() => setIsExpanded(!isExpanded)}
      >
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Shield className="w-5 h-5 text-blue-600" />
            <CardTitle className="text-base font-medium">
              {t('serviceReferrals.consent.title', 'Consent-Based Referral')}
            </CardTitle>
            {getConsentStatusBadge(consentContext.consent_status)}
            {getLinkTypeBadge(consentContext.link_type)}
          </div>
          <Button variant="ghost" size="sm">
            {isExpanded ? (
              <ChevronUp className="w-4 h-4" />
            ) : (
              <ChevronDown className="w-4 h-4" />
            )}
          </Button>
        </div>
        <CardDescription className="text-sm mt-1">
          {t('serviceReferrals.consent.createdFrom', 'Created from employee program consent on')} {formatDate(consentContext.consent_obtained_at)}
        </CardDescription>
      </CardHeader>

      {isExpanded && (
        <CardContent className="p-4 pt-2 space-y-4">
          {/* Program Information */}
          <div className="p-3 bg-white dark:bg-gray-800 rounded-lg border">
            <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2 flex items-center gap-1">
              <FileText className="w-4 h-4" />
              {t('serviceReferrals.consent.program', 'Program')}
            </h4>
            <p className="text-sm font-semibold text-gray-900 dark:text-gray-100">
              {consentContext.program_name}
            </p>
            {consentContext.program_description && (
              <p className="text-xs text-gray-500 mt-1">
                {consentContext.program_description}
              </p>
            )}
          </div>

          {/* Employee & Employer Information */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div className="p-3 bg-white dark:bg-gray-800 rounded-lg border">
              <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2 flex items-center gap-1">
                <User className="w-4 h-4" />
                {t('serviceReferrals.consent.employee', 'Employee')}
              </h4>
              <p className="text-sm font-semibold text-gray-900 dark:text-gray-100">
                {consentContext.employee_name}
              </p>
              {consentContext.employee_email && (
                <p className="text-xs text-gray-500">{consentContext.employee_email}</p>
              )}
            </div>

            <div className="p-3 bg-white dark:bg-gray-800 rounded-lg border">
              <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2 flex items-center gap-1">
                <Building2 className="w-4 h-4" />
                {t('serviceReferrals.consent.employer', 'Employer')}
              </h4>
              <p className="text-sm font-semibold text-gray-900 dark:text-gray-100">
                {consentContext.employer_name}
              </p>
            </div>
          </div>

          {/* Consent Details */}
          <div className="p-3 bg-white dark:bg-gray-800 rounded-lg border">
            <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2 flex items-center gap-1">
              <Calendar className="w-4 h-4" />
              {t('serviceReferrals.consent.consentDetails', 'Consent Details')}
            </h4>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
              <div>
                <p className="text-xs text-gray-500">{t('serviceReferrals.consent.consentDate', 'Consent Date')}</p>
                <p className="font-medium">{formatDate(consentContext.consent_obtained_at)}</p>
              </div>
              <div>
                <p className="text-xs text-gray-500">{t('serviceReferrals.consent.signatureMethod', 'Signature Method')}</p>
                <p className="font-medium capitalize">
                  {consentContext.signature_method || t('serviceReferrals.consent.electronic', 'Electronic')}
                </p>
              </div>
              {consentContext.consent_version && (
                <div>
                  <p className="text-xs text-gray-500">{t('serviceReferrals.consent.version', 'Version')}</p>
                  <p className="font-medium">{consentContext.consent_version}</p>
                </div>
              )}
              <div>
                <p className="text-xs text-gray-500">{t('serviceReferrals.consent.linkStatus', 'Link Status')}</p>
                <p className="font-medium">
                  {consentContext.link_active ? (
                    <span className="text-green-600">{t('serviceReferrals.consent.active', 'Active')}</span>
                  ) : (
                    <span className="text-red-600">{t('serviceReferrals.consent.inactive', 'Inactive')}</span>
                  )}
                </p>
              </div>
            </div>
          </div>

          {/* Actions */}
          {onViewConsent && (
            <div className="flex justify-end">
              <Button
                variant="outline"
                size="sm"
                onClick={() => onViewConsent(consentContext.consent_id)}
              >
                <ExternalLink className="w-4 h-4 mr-1" />
                {t('serviceReferrals.consent.viewFullConsent', 'View Full Consent')}
              </Button>
            </div>
          )}
        </CardContent>
      )}
    </Card>
  );
}

export default ConsentContextPanel;
